// src/components/TodayPage.jsx
// Today — Executive Twin Daily Command Center
// Six-layer daily intelligence brief. Every item opens The Floor with context loaded.

import React, { useState, useEffect, useCallback } from 'react';
import { useProfile } from '../context/UserProfileContext';
import './TodayPage.css';
import { apiFetch } from '../lib/api';
import { dismissQueueItem } from '../lib/situationQueue';
import NewLeadsLayer from './NewLeadsLayer';

const PRIORITY_COLORS = {
  '🔴 Hot':       { bg: 'var(--dm-red-light)', color: 'var(--dm-red)', border: '#fca5a5' },
  '🟡 Important': { bg: 'var(--dm-amber-light)', color: 'var(--dm-amber)', border: 'var(--dm-amber-tint)' },
  '🔵 Watch':     { bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-vivid)', border: '#93c5fd' },
  'Do First':     { bg: 'var(--dm-red-light)', color: 'var(--dm-red)', border: '#fca5a5' },
  'Do Today':     { bg: 'var(--dm-amber-light)', color: 'var(--dm-amber)', border: 'var(--dm-amber-tint)' },
  'This Week':    { bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-vivid)', border: '#93c5fd' },
};


function PriorityBadge({ priority }) {
  const style = PRIORITY_COLORS[priority] || { bg: '#f3f4f6', color: 'var(--dm-grey-600)', border: '#e5e7eb' };
  return (
    <span className="td-badge" style={{ background: style.bg, color: style.color, borderColor: style.border }}>
      {priority}
    </span>
  );
}

function LayerHeader({ icon, title, count, color }) {
  return (
    <div className="td-layer-header">
      <span className="td-layer-icon" style={{ color }}>{icon}</span>
      <span className="td-layer-title">{title}</span>
      {count > 0 && <span className="td-layer-count">{count}</span>}
    </div>
  );
}

const SOURCE_CONFIG = {
  queue:  { label: '⚡ Victor',  bg: '#eef2ff', color: 'var(--dm-indigo)' },
  task:   { label: '✅ Task',    bg: '#f0fdf4', color: '#15803d' },
  stale:  { label: '📊 Deal',   bg: '#fff7ed', color: '#c2410c' },
  signal: { label: '📡 Signal', bg: '#f0f9ff', color: '#0369a1' },
};

const PRIORITY_ORDER = {
  'Urgent': 0, 'High': 1, '🔴 Hot': 0, 'Do First': 0,
  'Medium': 2, '🟡 Important': 1, 'Do Today': 1,
  'Low': 3, '🔵 Watch': 2, 'This Week': 2,
};

function sortAttentionItems(items) {
  return [...items].sort((a, b) => {
    const oa = PRIORITY_ORDER[a.priority] ?? 3;
    const ob = PRIORITY_ORDER[b.priority] ?? 3;
    return oa - ob;
  });
}

function AttentionItem({ item, onOpenFloor, onDismiss, onActionError }) {
  const src = SOURCE_CONFIG[item.type] || SOURCE_CONFIG.task;
  const [dismissing, setDismissing] = React.useState(false);

  const handleClick = async () => {
    // Dismiss the queue item before opening the Floor so it does not reappear.
    //
    // This used to `await apiFetch(...)` directly and catch only a throw — but
    // apiFetch RESOLVES on a 500, so the await proved nothing except that the
    // network answered, and a failed dismiss still navigated away. The user
    // landed on the Floor and met the same item again on their next visit.
    // dismissQueueItem throws on a bad status AND on a success:false body
    // (P1.5 IA-8a).
    if (item.type === 'queue' && item.id) {
      setDismissing(true);
      try {
        await dismissQueueItem(item.id);
      } catch (err) {
        // Do NOT navigate: leaving for the Floor is what made this invisible.
        setDismissing(false);
        if (onActionError) onActionError(`Could not dismiss "${item.title}" — ${err.message}`);
        return;
      }
    }
    // For task items: mark Done in Notion
    if (item.type === 'task' && item.id) {
      try {
        await apiFetch('/api/tasks/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId: item.id, status: 'Done' }),
        });
      } catch (e) { console.warn('[Today] task done error:', e.message); }
    }
    // Remove from local state immediately so it doesn't re-appear
    if (onDismiss) onDismiss(item);
    onOpenFloor(item.floorContext);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDismissing(true);
    try {
      if (item.type === 'queue' && item.id) {
        await dismissQueueItem(item.id);
      } else if (item.type === 'task' && item.id) {
        await apiFetch('/api/tasks/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId: item.id, status: 'Done' }),
        });
      }
    } catch (err) {
      // Keep the row. Removing it after a failed write is what made this class
      // of bug invisible: the item returns on the next load and the user has no
      // way to know the dismiss never landed.
      setDismissing(false);
      if (onActionError) onActionError(`Could not dismiss "${item.title}" — ${err.message}`);
      return;
    }
    if (onDismiss) onDismiss(item);
  };

  return (
    <div
      className={`td-item td-item--attention ${item.urgent ? 'td-item--urgent' : ''} ${dismissing ? 'td-item--dismissing' : ''}`}
      onClick={handleClick}
      style={{ opacity: dismissing ? 0.4 : 1, pointerEvents: dismissing ? 'none' : 'auto' }}
    >
      <div className="td-item-main">
        <PriorityBadge priority={item.priority} />
        <span
          className="td-source-badge"
          style={{ background: src.bg, color: src.color }}
        >
          {src.label}
        </span>
        <span className="td-item-title">{item.title}</span>
      </div>
      {item.why && <div className="td-item-why">{item.why}</div>}
      <div className="td-item-actions">
        <div className="td-item-arrow">→</div>
        <button
          className="td-item-delete"
          onClick={handleDelete}
          title="Dismiss"
          style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dm-grey-500)', fontSize: 14, padding: '2px 4px' }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function OutreachItem({ item, onNavigate }) {
  return (
    <div className="td-item td-item--relationship"
      onClick={() => onNavigate && onNavigate('content', null, { draftId: item.draftId })}
      style={{ cursor: 'pointer' }}>
      <div className="td-item-main">
        <span className="td-rel-badge">Draft ready</span>
        <span className="td-item-title">{item.name}{item.accountName ? ` — ${item.accountName}` : ''}</span>
      </div>
      {item.note && <div className="td-item-why">{item.note}</div>}
      <div className="td-item-arrow">→ Open in Studio</div>
    </div>
  );
}

// Quinn Calendar event type colors — matches CalendarPage color map
const QUINN_EVENT_COLORS = {
  'Meeting': 'var(--dm-blue-mid)', 'Cadence Touchpoint': 'var(--dm-amber)', 'Email/LinkedIn': 'var(--dm-purple-vivid)',
  'Task Due': '#f59e0b', 'Birthday': '#ec4899', 'Anniversary': '#ec4899',
  'Kids Event': 'var(--dm-green-soft)', 'Personal': 'var(--dm-grey-600)', 'Vacation': '#06b6d4',
  'Health': '#ef4444', 'Subscription Renewal': '#f97316', 'Expense': '#84cc16',
};

function MeetingItem({ item, onOpenFloor, onPrepBrief, prepLoading, prepBrief }) {
  const isQuinnEvent  = item.source === 'quinn';
  const isPrivate     = item.isPrivate;
  const typeColor     = QUINN_EVENT_COLORS[item.eventType] || 'var(--dm-grey-600)';
  const showTypeBadge = isQuinnEvent && item.eventType && item.eventType !== 'Meeting';
  const canOpenFloor  = !isPrivate && item.floorContext;

  return (
    <div className="td-meeting-card">
      <div className="td-meeting-header">
        <span className="td-meeting-time">{item.time}</span>
        <div className="td-meeting-title-row">
          <span className="td-meeting-title">{item.title}</span>
          {showTypeBadge && (
            <span
              className="td-event-type-badge"
              style={{ background: typeColor + '1a', color: typeColor, border: `1px solid ${typeColor}33` }}
            >
              {item.eventType}
            </span>
          )}
          {item.priority === 'High' && (
            <span className="td-event-urgent-badge">⚠️ Urgent</span>
          )}
        </div>
        {item.accountName && <span className="td-meeting-account">{item.accountName}</span>}
      </div>
      {item.notes && (
        <div className="td-meeting-notes">{item.notes}</div>
      )}
      <div className="td-meeting-actions">
        {item.hasPrepBrief && (
          <button
            className="td-prep-btn"
            onClick={() => onPrepBrief(item)}
            disabled={prepLoading}
          >
            {prepLoading ? '⏳ Generating...' : '⚡ Victor Brief'}
          </button>
        )}
        {canOpenFloor && (
          <button
            className="td-floor-btn"
            onClick={() => onOpenFloor(item.floorContext)}
          >
            Open in The Floor →
          </button>
        )}
      </div>
      {prepBrief && (
        <div className="td-prep-brief">
          <div className="td-prep-brief-label">Victor's Meeting Brief</div>
          <div className="td-prep-brief-content" dangerouslySetInnerHTML={{ __html: prepBrief.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
        </div>
      )}
    </div>
  );
}

// ── Victor Executive Brief Card ───────────────────────────────────────────────
function VictorExecutiveBriefCard({ brief }) {
  const [activeTab, setActiveTab]     = React.useState('directive');
  const [collapsed, setCollapsed]     = React.useState(false);
  const [dismissed, setDismissed]     = React.useState(false);
  const [sharing, setSharing]         = React.useState(false);
  const [shareToast, setShareToast]   = React.useState(null);

  // Tenant-sourced recipient — set in Profile & Settings, not hardcoded.
  const { email: recipientEmail } = useProfile();

  if (dismissed || !brief) return null;

  const timeLabel = brief?.cachedAt || brief?.generatedAt
    ? new Date(brief.cachedAt || brief.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
    : null;

  const dateLabel = brief?.cachedAt || brief?.generatedAt
    ? new Date(brief.cachedAt || brief.generatedAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York' })
    : null;

  const tabs = [
    { id: 'directive',     label: '⚡ Directive' },
    { id: 'industry',      label: '🌐 Industry' },
    { id: 'competitive',   label: '🎯 Competitive' },
    { id: 'selma',         label: '🔬 Selma' },
    { id: 'pipelineState', label: '📊 Pipeline' },
  ];

  const handleShare = async () => {
    if (!brief) return;
    if (!recipientEmail) {
      setShareToast('⚠️ Set your email in Profile & Settings to share briefs');
      setTimeout(() => setShareToast(null), 3000);
      return;
    }
    setSharing(true);
    try {
      const body = [
        "VICTOR'S EXECUTIVE BRIEF",
        dateLabel || '',
        '',
        '=== DIRECTIVE ===',
        brief.directive || '',
        brief.victorCall ? '\nVictor\'s Call: ' + brief.victorCall : '',
        '',
        '=== INDUSTRY ===',
        brief.industry || '',
        '',
        '=== COMPETITIVE ===',
        brief.competitive || '',
        '',
        '=== SELMA ===',
        brief.selma || '',
        '',
        '=== PIPELINE ===',
        brief.pipelineState || '',
      ].join('\n');

      // Send through Graph as the signed-in user (P0.23 A3)
      const res = await apiFetch('/api/comms/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipientEmail,
          subject: "Victor's Executive Brief — " + (dateLabel || 'Today'),
          body: body.replace(/\n/g, '<br>'),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setShareToast('✅ Sent to ' + recipientEmail);
      } else {
        throw new Error('send failed');
      }
    } catch {
      // Fallback: mailto
      const mailtoSubject = encodeURIComponent("Victor's Executive Brief — " + (dateLabel || 'Today'));
      const body = encodeURIComponent([
        "VICTOR'S EXECUTIVE BRIEF",
        dateLabel || '',
        '',
        '=== DIRECTIVE ===',
        brief.directive || '',
        brief.victorCall ? '\nVictor\'s Call: ' + brief.victorCall : '',
        '',
        '=== INDUSTRY ===',
        brief.industry || '',
        '',
        '=== COMPETITIVE ===',
        brief.competitive || '',
      ].join('\n').slice(0, 1800));
      window.open('mailto:' + recipientEmail + '?subject=' + mailtoSubject + '&body=' + body);
      setShareToast('📧 Opened in mail client');
    } finally {
      setSharing(false);
      setTimeout(() => setShareToast(null), 3000);
    }
  };

  return (
    <div className="td-victor-read">
      <div className="td-victor-read-header">
        <div className="td-victor-read-label">
          <span className="td-victor-read-icon">⚡</span>
          <div>
            <div className="td-victor-read-title">Victor's Executive Brief</div>
            {timeLabel && <div className="td-victor-read-time">Generated {timeLabel} · Jarvis</div>}
            {!brief && <div className="td-victor-read-time" style={{ color: 'var(--dm-grey-500)' }}>Jarvis generates this at 3 AM · Check back tomorrow</div>}
          </div>
        </div>
        <div className="td-victor-read-controls">
          {brief && (
            <>
              <button className="td-victor-share-btn" onClick={handleShare} disabled={sharing} title="Share via email">
                {sharing ? '⏳' : '↗ Share'}
              </button>
              <button className="td-morning-brief-toggle" onClick={() => setCollapsed(c => !c)}>
                {collapsed ? 'Expand ↓' : 'Collapse ↑'}
              </button>
            </>
          )}
          <button className="td-morning-brief-dismiss" onClick={() => setDismissed(true)}>✕</button>
        </div>
      </div>

      {shareToast && (
        <div className="td-victor-share-toast">{shareToast}</div>
      )}

      {!collapsed && brief && (
        <>
          <div className="td-victor-tabs">
            {tabs.map(t => (
              <button key={t.id}
                className={'td-victor-tab' + (activeTab === t.id ? ' active' : '')}
                onClick={() => setActiveTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="td-victor-read-body">
            {activeTab === 'directive' && (
              <>
                <div className="td-victor-section td-victor-directive">{brief.directive || 'No directive generated.'}</div>
                {brief.victorCall && (
                  <div className="td-victor-call">
                    <span className="td-victor-call-label">Victor's Call:</span> {brief.victorCall}
                  </div>
                )}
              </>
            )}
            {activeTab === 'industry'      && <div className="td-victor-section">{brief.industry      || 'No industry intelligence available.'}</div>}
            {activeTab === 'competitive'   && <div className="td-victor-section">{brief.competitive   || 'No competitive intelligence available.'}</div>}
            {activeTab === 'selma'         && <div className="td-victor-section">{brief.selma         || 'Selma did not surface material findings since the last brief.'}</div>}
            {activeTab === 'pipelineState' && <div className="td-victor-section">{brief.pipelineState || 'No pipeline state generated.'}</div>}
          </div>
        </>
      )}
    </div>
  );
}



// ── Morning Brief Card ───────────────────────────────────────────────────────
function MorningBriefCard({ brief }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);

  if (dismissed) return null;

  const s = brief.structured;
  const timeLabel = brief.generatedAt
    ? new Date(brief.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
    : null;

  const hasCadences        = s?.cadences?.length > 0;
  const hasPursuitCadences = s?.pursuitCadences?.length > 0;
  const hasMeetings        = s?.meetings?.length > 0;
  const hasDrafts          = s?.drafts?.length > 0;
  const hasTasks           = s?.tasks?.length > 0;
  const counts             = s?.counts || {};

  const countParts = [
    counts.cadences         ? counts.cadences         + ' cadence'  + (counts.cadences         !== 1 ? 's' : '') : null,
    counts.pursuitCadences  ? counts.pursuitCadences  + ' pursuit'  + (counts.pursuitCadences  !== 1 ? 's' : '') : null,
    counts.drafts           ? counts.drafts           + ' draft'    + (counts.drafts           !== 1 ? 's' : '') : null,
    counts.tasks            ? counts.tasks            + ' task'     + (counts.tasks            !== 1 ? 's' : '') : null,
    counts.meetings         ? counts.meetings         + ' meeting'  + (counts.meetings         !== 1 ? 's' : '') : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="td-morning-brief">
      <div className="td-morning-brief-header">
        <div className="td-morning-brief-label">
          <span className="td-morning-brief-icon">👑</span>
          <div>
            <div className="td-morning-brief-title">Quinn's Morning Brief</div>
            {timeLabel && <div className="td-morning-brief-date">Generated {timeLabel}</div>}
          </div>
        </div>
        <button className="td-morning-brief-toggle" onClick={() => setCollapsed(c => !c)}>
          {collapsed ? 'Expand ↓' : 'Collapse ↑'}
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="td-morning-brief-sections">
            {hasCadences && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-section-label">Cadence touchpoints</div>
                {s.cadences.map((c, i) => (
                  <div key={i} className="td-morning-brief-item">
                    <span className="td-morning-brief-dot" />
                    <span>{c.name}{c.overdue ? ' ⚠️' : ''}{c.notes && c.notes !== 'Scheduled cadence touchpoint' ? ` — ${c.notes}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
            {hasPursuitCadences && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-section-label">Pursuit Cadence</div>
                {s.pursuitCadences.slice(0, 10).map((p, i) => {
                  const sev = p?.severity;
                  const dotClass = sev === 'stalled'
                    ? 'td-morning-brief-dot td-morning-brief-dot--stalled'
                    : sev === 'overdue'
                    ? 'td-morning-brief-dot td-morning-brief-dot--overdue'
                    : sev === 'due'
                    ? 'td-morning-brief-dot td-morning-brief-dot--due'
                    : 'td-morning-brief-dot';
                  return (
                    <div key={i} className="td-morning-brief-item">
                      <span className={dotClass} />
                      <span>{p?.line || ''}</span>
                    </div>
                  );
                })}
                {s.pursuitCadences.length > 10 && (
                  <div className="td-morning-brief-item td-morning-brief-more">+{s.pursuitCadences.length - 10} more</div>
                )}
              </div>
            )}
            {hasDrafts && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-section-label">Drafts pending approval</div>
                {s.drafts.slice(0, 4).map((d, i) => (
                  <div key={i} className="td-morning-brief-item">
                    <span className="td-morning-brief-dot" />
                    <span>{d.to ? d.to + ' — ' : ''}{d.subject}</span>
                  </div>
                ))}
                {s.drafts.length > 4 && (
                  <div className="td-morning-brief-item td-morning-brief-more">+{s.drafts.length - 4} more</div>
                )}
              </div>
            )}
            {hasTasks && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-section-label">Tasks due today</div>
                {s.tasks.map((t, i) => (
                  <div key={i} className="td-morning-brief-item">
                    <span className="td-morning-brief-dot" />
                    <span>{t.name}</span>
                  </div>
                ))}
              </div>
            )}
            {hasMeetings && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-section-label">Meetings</div>
                {s.meetings.map((m, i) => (
                  <div key={i} className="td-morning-brief-item">
                    <span className="td-morning-brief-dot" />
                    <span>{m.time ? m.time + ' — ' : ''}{m.name}{m.account ? ` (${m.account})` : ''}</span>
                  </div>
                ))}
              </div>
            )}
            {!hasCadences && !hasPursuitCadences && !hasDrafts && !hasTasks && !hasMeetings && (
              <div className="td-morning-brief-section">
                <div className="td-morning-brief-item td-morning-brief-more">Nothing scheduled for today.</div>
              </div>
            )}
          </div>
          <div className="td-morning-brief-footer">
            <span className="td-morning-brief-counts">{countParts || 'No items today'}</span>
            <button className="td-morning-brief-dismiss" onClick={() => setDismissed(true)}>
              Dismiss for today ✕
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function TodayPage({ onNavigate }) {
  const [brief,         setBrief]        = useState(null);
  const [loading,       setLoading]      = useState(true);
  const [error,         setError]        = useState(null);
  const [prepLoading,   setPrepLoading]  = useState(null);
  const [prepBriefs,    setPrepBriefs]   = useState({});
  const [showHistory,   setShowHistory]  = useState(false);
  const [historyBriefs, setHistoryBriefs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryBrief, setSelectedHistoryBrief] = useState(null);
  const [dismissedIds,  setDismissedIds] = useState(new Set()); // locally dismissed items
  // Separate from `error`, which is the page-level load failure. A dismiss that
  // fails must leave the brief on screen and say so (P1.5 IA-8a).
  const [actionError,   setActionError]  = useState(null);

  const loadBrief = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDismissedIds(new Set()); // clear dismissed set on full refresh
    try {
      const res  = await apiFetch('/api/today/brief');
      const data = await res.json();
      if (data.success) setBrief(data);  // data includes morningBrief + victorExecutiveBrief at top level
      else setError(data.error || 'Failed to load');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBrief(); }, [loadBrief]);

  // M5 fix: refresh when tab becomes visible again (user returns from Floor)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadBrief();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadBrief]);

  const handleDismiss = useCallback((item) => {
    const key = item.id || item.title;
    setDismissedIds(prev => new Set([...prev, key]));
  }, []);

  const openInFloor = (floorContext) => {
    if (!floorContext) return;
    // Pass returnTo so The Floor shows a "← Today" back button
    onNavigate('floor', null, { ...floorContext, returnTo: 'today' });
  };

  const generatePrepBrief = async (meeting) => {
    if (!meeting.accountId) return;
    setPrepLoading(meeting.title);
    try {
      const res  = await apiFetch('/api/today/brief', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ accountId: meeting.accountId, meetingTitle: meeting.title }),
      });
      const data = await res.json();
      if (data.success && data.brief) {
        setPrepBriefs(prev => ({ ...prev, [meeting.title]: data.brief }));
      }
    } catch (err) {
      console.error('[Today] prep brief error:', err.message);
    } finally {
      setPrepLoading(null);
    }
  };

  if (loading) return (
    <div className="td-loading">
      <div className="td-loading-inner">
        <div className="td-loading-dots">
          <span /><span /><span />
        </div>
        <div className="td-loading-text">Victor is reviewing your day...</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="td-error-state">
      <div>⚠️ {error}</div>
      <button onClick={loadBrief} className="td-retry-btn">Retry</button>
    </div>
  );

  if (!brief) return null;

  // Layers are defaulted, not assumed. GET /api/today/brief normally returns
  // it (api/today/brief.js:474), but the guard above only proves `brief` is
  // truthy — any success:true response without layers crashed the whole page
  // with "Cannot read properties of undefined (reading 'attention')". The `?.`
  // on .attention shows the nesting was already known to be optional; the level
  // above it was the one missing a guard. Found by the Phase 5.2 smoke test,
  // which mounts this page against an empty payload.
  // Each layer defaults to [], not just `layers` to {} — the render below does
  // layers.day.length and layers.relationships.map() unguarded, so an empty
  // object would move the same crash three lines down rather than fix it.
  const { victorExecutiveBrief } = brief;
  const layers = {
    attention: [], intelligence: [], pursuits: [], relationships: [], day: [], team: [],
    ...(brief.layers || {}),
  };
  const totalAttention = layers.attention?.length || 0;

  return (
    <div className="td-container">
      {/* Today had no page header at all — only a bare Refresh button — so it
          was the one screen that never said where you were. Matches the studio
          format used by Research, The Studio and Victor: mark, name, one line
          of purpose, action on the right. */}
      <div className="td-page-header">
        <div className="td-page-header-left">
          <span className="dm-page-mark dm-page-mark--today">📅</span>
          <div>
            <div className="td-page-title">Today</div>
            <div className="td-page-sub">Your daily brief — what changed overnight and what needs you</div>
          </div>
        </div>
        <div className="td-toolbar">
          <button className="td-refresh-btn" onClick={loadBrief}>↻ Refresh</button>
        </div>
      </div>

      {actionError && (
        <div className="td-action-error" role="alert">
          ⚠️ {actionError}
          <button className="td-action-error-dismiss"
                  onClick={() => setActionError(null)}
                  aria-label="Dismiss message">×</button>
        </div>
      )}

      <div className="td-layers">

        {/* Victor's Read — strategic daily synthesis, above Quinn's operational brief */}
        {/* Victor's Executive Brief — Jarvis-orchestrated, always shown (with generate button if not cached) */}
        <VictorExecutiveBriefCard
          brief={victorExecutiveBrief || null}
        />
        {/* Brief Archive link — only show once briefs exist */}
        {victorExecutiveBrief && (
          <div className="td-brief-archive-link">
          <button onClick={async () => {
            setShowHistory(true);
            if (historyBriefs.length === 0) {
              setHistoryLoading(true);
              try {
                const res = await apiFetch('/api/jarvis/brief-history?limit=14');
                const data = await res.json();
                if (data.success) setHistoryBriefs(data.briefs);
              } catch { /* ignore */ } finally {
                setHistoryLoading(false);
              }
            }
          }}>
            📁 Brief Archive →
          </button>
        </div>
        )}

        {/* History panel overlay */}
        {showHistory && (
          <div className="td-history-overlay" onClick={() => { setShowHistory(false); setSelectedHistoryBrief(null); }}>
            <div className="td-history-panel" onClick={e => e.stopPropagation()}>
              <div className="td-history-header">
                <span className="td-history-title">
                  {selectedHistoryBrief ? '← ' : ''}Victor's Brief Archive
                </span>
                <button className="td-history-close" onClick={() => { setShowHistory(false); setSelectedHistoryBrief(null); }}>✕</button>
              </div>
              {!selectedHistoryBrief ? (
                <div className="td-history-list">
                  {historyLoading && <div className="td-history-empty">Loading archive...</div>}
                  {!historyLoading && historyBriefs.length === 0 && (
                    <div className="td-history-empty">No archived briefs yet. Jarvis generates one each morning at 3 AM.</div>
                  )}
                  {historyBriefs.map(b => (
                    <div key={b.id} className="td-history-item" onClick={() => setSelectedHistoryBrief(b)}>
                      <div className="td-history-item-date">{b.dateLabel}</div>
                      {b.victorCall && (
                        <div className="td-history-item-call">"{b.victorCall}"</div>
                      )}
                      <div className="td-history-item-arrow">→</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="td-history-detail">
                  <button className="td-history-back" onClick={() => setSelectedHistoryBrief(null)}>← Back to archive</button>
                  <div className="td-history-detail-date">{selectedHistoryBrief.dateLabel}</div>
                  {selectedHistoryBrief.directive && (
                    <div className="td-history-section">
                      <div className="td-history-section-label">⚡ DIRECTIVE</div>
                      <div className="td-history-section-body td-victor-directive">{selectedHistoryBrief.directive}</div>
                      {selectedHistoryBrief.victorCall && (
                        <div className="td-victor-call" style={{ marginTop: 8 }}>
                          <span className="td-victor-call-label">Victor's Call:</span> {selectedHistoryBrief.victorCall}
                        </div>
                      )}
                    </div>
                  )}
                  {selectedHistoryBrief.industry && (
                    <div className="td-history-section">
                      <div className="td-history-section-label">🌐 INDUSTRY</div>
                      <div className="td-history-section-body">{selectedHistoryBrief.industry}</div>
                    </div>
                  )}
                  {selectedHistoryBrief.competitive && (
                    <div className="td-history-section">
                      <div className="td-history-section-label">🎯 COMPETITIVE</div>
                      <div className="td-history-section-body">{selectedHistoryBrief.competitive}</div>
                    </div>
                  )}
                  {selectedHistoryBrief.accounts && (
                    <div className="td-history-section">
                      <div className="td-history-section-label">📊 ACCOUNTS</div>
                      <div className="td-history-section-body">{selectedHistoryBrief.accounts}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Morning Brief — Quinn's structured daily handoff, above the Action Queue */}
        {brief.morningBrief && (
          <MorningBriefCard brief={brief.morningBrief} />
        )}

        {/* Stage 1 of Today: triage what arrived overnight, before working the
            queue. Moved here from MorningPlanningTool step 1 (IA-7), which was
            this app's only lead-triage entry point. Self-fetching, so it renders
            whether or not /api/today/brief has anything to say. */}
        <NewLeadsLayer onNavigate={onNavigate} />

        {/* Layer 1: Today's Tasks — Victor Queue + Tasks, sorted by priority */}
        {layers.attention?.length > 0 && (() => {
          const visibleItems = sortAttentionItems(layers.attention).filter(
            item => !dismissedIds.has(item.id || item.title)
          );
          return visibleItems.length > 0 ? (
            <div className="td-layer">
              <LayerHeader icon="🎯" title="Today's Tasks" count={visibleItems.length} color="var(--dm-red)" />
              <div className="td-items td-items--scrollable">
                {visibleItems.map((item, i) => (
                  <AttentionItem key={item.id || i} item={item} onOpenFloor={openInFloor} onDismiss={handleDismiss} onActionError={setActionError} />
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Layer 5: Your Day — shown prominently when there are meetings */}
        {layers.day?.length > 0 && (
          <div className="td-layer">
            <LayerHeader icon="📅" title="Your Day" count={layers.day.length} color="var(--dm-purple-vivid)" />
            <div className="td-meetings">
              {layers.day.map((meeting, i) => (
                <MeetingItem
                  key={i}
                  item={meeting}
                  onOpenFloor={openInFloor}
                  onPrepBrief={generatePrepBrief}
                  prepLoading={prepLoading === meeting.title}
                  prepBrief={prepBriefs[meeting.title] || null}
                />
              ))}
            </div>
          </div>
        )}

        {/* Layer 4: Outreach Cadence */}
        {layers.relationships?.length > 0 && (
          <div className="td-layer">
            <LayerHeader icon="✉️" title="Outreach Cadence" count={layers.relationships.length} color="var(--dm-purple-vivid)" />
            <div className="td-items">
              {layers.relationships.map((item, i) => (
                <OutreachItem key={i} item={item} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {totalAttention === 0 && !layers.day?.length && (
          <div className="td-clear-day">
            <div className="td-clear-icon">✅</div>
            <div className="td-clear-title">You're clear</div>
            <div className="td-clear-sub">No urgent items. A good day to be proactive.</div>
            <button className="td-floor-main-btn" onClick={() => openInFloor({ type: 'nav' })}>
              Open The Floor →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TodayPage;
